import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Result,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery } from 'react-apollo';
import { get } from 'lodash';
import { ADMIN_ENVIRONMENT } from '../pages/apollo_queries';
import { ADMIN_CREATE_ADMIN } from '../pages/apollo_mutations';

// Creating an ADMIN (TL, 2026-09-12). Until now the only way was editing Mongo by hand, so this screen
// is a new operation, not a migration. Two things it must get right and the backend cannot:
//
//  1. NOBODY TYPES A PASSWORD HERE (TL, same day). The account is created without a usable one and the
//     new admin gets the SAME password-recovery email the platform already sends, so the password only
//     ever exists in their head. That is why this form has no password field and the result panel has
//     nothing to copy — what it has to say is whether the email went out.
//  2. THE PHONE IS PER ENVIRONMENT. `adminEnvironment.suggestedAdminPhone` carries the handset the
//     deployment offers for the second factor — sandbox hands out the team's shared test number because
//     its code arrives in Slack. Dev and production answer null and the field starts empty: in dev any
//     code is accepted anyway, and in production an admin's second factor must be their own phone.
//
// The number travels in the STORED shape: ten national digits, no country code. The platform is US-only
// (`toE164` rebuilds every number with +1), so the +1 is a fixed prefix on the input, not a choice.

const PHONE_DIGITS = 10;

// What the operator typed, reduced to what the column stores. Separators are welcome — `(512) 555-9876`
// is how a phone is read — and a leading US country code is dropped rather than rejected.
// One name box, not two (TL, 2026-09-12). The backend is the one that splits it into the `firstName`
// and `lastName` the person document has always had, so the rule lives in one place and its errors name
// the box the operator is actually looking at. Here we only tidy what was typed.
const collapseSpaces = (raw) => (raw || '').trim().replace(/\s+/g, ' ');

const toNationalDigits = (raw) => {
  const digits = (raw || '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

const ENV_TAG = {
  production: 'red',
  sandbox: 'gold',
  dev: 'blue',
  local: 'default',
};

// The created admin. What the operator has to know is not a credential any more but whether the email
// left: on the failure branch the account exists and still cannot be signed into, and only this panel
// says so.
function CreatedPanel({ created, onClose }) {
  const admin = get(created, 'admin', {});
  const emailSent = get(created, 'passwordEmailSent');
  return (
    <Result
      status={emailSent ? 'success' : 'warning'}
      style={{ padding: '8px 0 0' }}
      title={`${admin.firstName} ${admin.lastName} is on the team`}
      subTitle={
        admin.phoneNumber
          ? 'They already carry a phone for the second factor.'
          : 'They will be asked to enrol a phone for the second factor the first time they sign in.'
      }
      extra={[
        <Button key="done" type="primary" onClick={onClose}>
          Done
        </Button>,
      ]}
    >
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Email">{admin.email}</Descriptions.Item>
        {admin.phoneNumber && (
          <Descriptions.Item label="Phone">+1 {admin.phoneNumber}</Descriptions.Item>
        )}
      </Descriptions>
      <Alert
        style={{ marginTop: 16, textAlign: 'left' }}
        type={emailSent ? 'success' : 'error'}
        showIcon
        message={
          emailSent
            ? 'We emailed them a link to set their password'
            : 'The email could not be sent'
        }
        description={
          emailSent
            ? 'The link is good for one hour. They choose their own password there and then sign in here — there is nothing for you to hand over. If it never arrives, they can ask for another one from the sign-in screen.'
            : 'The account exists but nobody can sign into it yet: no password was ever set. Ask them to use “Forgot your password?” on the sign-in screen, and tell us — an email that does not leave is a configuration problem, not a retry.'
        }
      />
    </Result>
  );
}

function NewAdminModal({ visible, onClose, onCreated }) {
  const [form] = Form.useForm();
  const [created, setCreated] = useState(null);
  const { data: envData } = useQuery(ADMIN_ENVIRONMENT, { skip: !visible });
  const [createAdmin, { loading }] = useMutation(ADMIN_CREATE_ADMIN);

  const environment = get(envData, 'adminEnvironment.name', '');
  const suggestedPhone = get(envData, 'adminEnvironment.suggestedAdminPhone') || '';

  // The environment answers after the form is already mounted, so the prefill is an effect and not an
  // initial value. It only ever fills an untouched field: an operator who typed a number keeps it.
  useEffect(() => {
    if (!visible || !suggestedPhone) return;
    if (!form.isFieldTouched('phoneNumber') && !form.getFieldValue('phoneNumber')) {
      form.setFieldsValue({ phoneNumber: suggestedPhone });
    }
  }, [visible, suggestedPhone, form]);

  const close = () => {
    form.resetFields();
    setCreated(null);
    onClose();
  };

  const done = async () => {
    close();
    if (onCreated) await onCreated();
  };

  const submit = async (values) => {
    const phoneNumber = toNationalDigits(values.phoneNumber);
    try {
      const { data } = await createAdmin({
        variables: {
          data: {
            email: values.email.trim(),
            fullName: collapseSpaces(values.fullName),
            phoneNumber: phoneNumber || undefined,
          },
        },
      });
      setCreated(get(data, 'adminCreateAdmin'));
    } catch (error) {
      // A taken address is not a failure of the operation, it is a correction to make in one field:
      // it lands ON the field, which survives, instead of a toast that scrolls away. The backend's
      // own words (the promotion it refuses to do) stay out of a toast nobody asked for.
      const code = get(error, 'graphQLErrors[0].extensions.code');
      if (code === 'ADMIN_EMAIL_TAKEN') {
        form.setFields([
          {
            name: 'email',
            errors: ['Someone on the platform already signs in with this address'],
          },
        ]);
        return;
      }
      message.error((error.message || '').replace('GraphQL error: ', ''));
    }
  };

  return (
    <Modal
      visible={visible}
      destroyOnClose
      width={560}
      title={
        <>
          New admin{' '}
          {environment && (
            <Tag color={ENV_TAG[environment] || 'default'} style={{ marginLeft: 8 }}>
              {environment}
            </Tag>
          )}
        </>
      }
      onCancel={created ? done : close}
      footer={created ? null : undefined}
      okText="Create admin"
      okButtonProps={{ loading }}
      onOk={() => form.submit()}
    >
      {created ? (
        <CreatedPanel created={created} onClose={done} />
      ) : (
        <>
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message="An admin sees every investor and can create and edit notes"
            description="Only create one for someone on the team. You do not set their password: we email them a link so they choose it themselves, and they set up their second factor when they first sign in."
          />
          <Form form={form} layout="vertical" onFinish={submit} preserve={false}>
            <Form.Item
              name="fullName"
              label="Full name"
              extra="First and last name, as it should read in the people list."
              rules={[
                { required: true, message: 'A name is required' },
                {
                  validator: (_, value) =>
                    collapseSpaces(value).includes(' ')
                      ? Promise.resolve()
                      : Promise.reject(new Error('Enter first and last name')),
                },
              ]}
            >
              <Input autoFocus maxLength={200} placeholder="Ada Lovelace" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              extra="This is how they sign in, and where the link to set their password is sent. It cannot already belong to anyone on the platform."
              rules={[
                { required: true, message: 'An email is required' },
                { type: 'email', message: 'That is not a valid email' },
              ]}
            >
              <Input placeholder="admin+002@constlending.com" />
            </Form.Item>
            <Form.Item
              name="phoneNumber"
              label="Phone for the second factor"
              extra={
                suggestedPhone
                  ? `This environment shares one test handset (${suggestedPhone}) — its verification code arrives in the team's Slack. Change it only if you know the number can receive SMS.`
                  : 'Optional. Leave it empty and they enrol their own phone the first time they sign in.'
              }
              rules={[
                {
                  validator: (_, value) => {
                    const digits = toNationalDigits(value);
                    if (!digits) return Promise.resolve();
                    return digits.length === PHONE_DIGITS
                      ? Promise.resolve()
                      : Promise.reject(new Error('A US number: 10 digits, no country code'));
                  },
                },
              ]}
            >
              <Input addonBefore="+1" placeholder="5125559876" />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}

export default NewAdminModal;
